import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '@/app/auth/auth.service';

type NavGroup = 'Core' | 'Governance' | 'Operations' | 'Settings';

export interface NavItem {
    label: string;
    icon: string;
    routerLink: string;
    group: NavGroup;
    badgeKey?: 'validation' | 'approvals';
    permission?: string;
}

const NAV_ITEMS: readonly NavItem[] = [
    { label: 'Quick View', icon: 'pi pi-home', routerLink: '/triage', group: 'Core', permission: 'quick-view:view' },
    { label: 'Dashboard', icon: 'pi pi-th-large', routerLink: '/dashboard', group: 'Core' },
    { label: 'Requests', icon: 'pi pi-inbox', routerLink: '/requests', group: 'Core' },
    { label: 'Catalog', icon: 'pi pi-list', routerLink: '/catalog', group: 'Core' },
    { label: 'Local adaptations', icon: 'pi pi-sitemap', routerLink: '/local-adaptations', group: 'Core' },

    { label: 'Validation', icon: 'pi pi-shield', routerLink: '/validation', group: 'Governance', badgeKey: 'validation' },
    { label: 'Approvals', icon: 'pi pi-check-circle', routerLink: '/approvals', group: 'Governance', badgeKey: 'approvals' },
    { label: 'Workflows', icon: 'pi pi-sliders-h', routerLink: '/workflows', group: 'Governance' },
    { label: 'Audit log', icon: 'pi pi-file', routerLink: '/audit-log', group: 'Governance' },

    { label: 'Document Extraction', icon: 'pi pi-file-import', routerLink: '/extraction', group: 'Operations' },
    { label: 'Ingestion', icon: 'pi pi-download', routerLink: '/ingestion', group: 'Operations' },
    { label: 'Deployment', icon: 'pi pi-upload', routerLink: '/deployment', group: 'Operations' },
    { label: 'Adapters', icon: 'pi pi-code', routerLink: '/adapters', group: 'Operations' },
    { label: 'Integrations', icon: 'pi pi-share-alt', routerLink: '/integrations', group: 'Operations' },

    { label: 'Access Management', icon: 'pi pi-users', routerLink: '/settings/users-groups', group: 'Settings', permission: 'user:manage' },
    { label: 'AI Gateway', icon: 'pi pi-microchip', routerLink: '/settings/ai-gateway', group: 'Settings', permission: 'ai-gateway:view' },
    { label: 'Tag management', icon: 'pi pi-tag', routerLink: '/settings/tags', group: 'Settings' }
] as const;

const NAV_GROUPS: readonly NavGroup[] = ['Core', 'Governance', 'Operations', 'Settings'] as const;

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule, BadgeModule, RippleModule],
    templateUrl: './app-sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSidebarComponent {
    private readonly auth = inject(AuthService);

    readonly validationBadge = input<number | null | undefined>(undefined);
    readonly approvalsBadge = input<number | null | undefined>(undefined);

    readonly groups = NAV_GROUPS;

    readonly itemsByGroup = computed(() => {
        const out = new Map<NavGroup, NavItem[]>();
        for (const group of NAV_GROUPS) out.set(group, []);
        for (const item of NAV_ITEMS) {
            if (item.permission && !this.auth.hasPermission(item.permission)) continue;
            out.get(item.group)!.push(item);
        }
        return out;
    });

    badgeFor(item: NavItem): number | null | undefined {
        if (item.badgeKey === 'validation') return this.validationBadge();
        if (item.badgeKey === 'approvals') return this.approvalsBadge();
        return undefined;
    }
}
